export const CONSENT_PREFERENCES_KEY = 'consent-preferences-v2';
export const CONSENT_MODAL_SNOOZE_KEY = 'consent-modal-snooze-until';
export const CONSENT_MODAL_OPEN_EVENT = 'open-consent-modal';
export const CONSENT_PREFERENCES_UPDATED_EVENT = 'consent-preferences-updated';
export const CONSENT_VERSION = 2;
export const CONSENT_MODAL_SNOOZE_MS = 6 * 60 * 60 * 1000;

const DEFAULT_CATEGORIES = Object.freeze({
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
});

const normalizeCategories = (rawCategories = {}) => ({
  necessary: true,
  functional: rawCategories.functional === true,
  analytics: rawCategories.analytics === true,
  marketing: rawCategories.marketing === true,
});

const readLegacyBoolean = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;
    return raw === 'true';
  } catch (_) {
    return null;
  }
};

const parseIsoTimestamp = (value) => {
  if (typeof value !== 'string' || !value.trim()) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
};

const parseStoredConsent = (raw) => {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.version !== CONSENT_VERSION) return null;

    const updatedAt = parseIsoTimestamp(parsed.updated_at);
    if (!updatedAt) return null;

    return {
      version: CONSENT_VERSION,
      region: parsed.region === 'non_eu' ? 'non_eu' : 'eu',
      categories: normalizeCategories(parsed.categories),
      decided_at: parseIsoTimestamp(parsed.decided_at),
      updated_at: updatedAt,
      source: typeof parsed.source === 'string' ? parsed.source : 'migration',
    };
  } catch (_) {
    return null;
  }
};

const persistConsentPreferences = (preferences) => {
  try {
    localStorage.setItem(CONSENT_PREFERENCES_KEY, JSON.stringify(preferences));
    return true;
  } catch (_) {
    return false;
  }
};

const mirrorLegacyConsentKeys = (categories) => {
  try {
    localStorage.setItem('analytics-consent', String(categories.analytics));
  } catch (_) {
    // Best effort only.
  }
};

const buildPreferences = ({
  region = 'eu',
  categories = DEFAULT_CATEGORIES,
  decidedAt = null,
  source = 'migration',
  updatedAt = null,
}) => {
  const normalizedUpdatedAt = parseIsoTimestamp(updatedAt) || new Date().toISOString();
  const normalizedDecidedAt = decidedAt ? parseIsoTimestamp(decidedAt) : null;

  return {
    version: CONSENT_VERSION,
    region: region === 'non_eu' ? 'non_eu' : 'eu',
    categories: normalizeCategories(categories),
    decided_at: normalizedDecidedAt,
    updated_at: normalizedUpdatedAt,
    source,
  };
};

export const getConsentPreferences = () => {
  try {
    const raw = localStorage.getItem(CONSENT_PREFERENCES_KEY);
    return parseStoredConsent(raw);
  } catch (_) {
    return null;
  }
};

export const setConsentPreferences = ({
  region = 'eu',
  categories = DEFAULT_CATEGORIES,
  decidedAt = null,
  source = 'modal',
  updatedAt = null,
}) => {
  const preferences = buildPreferences({
    region,
    categories,
    decidedAt,
    source,
    updatedAt,
  });

  if (!persistConsentPreferences(preferences)) {
    return null;
  }

  mirrorLegacyConsentKeys(preferences.categories);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(CONSENT_PREFERENCES_UPDATED_EVENT));
  }
  return preferences;
};

export const migrateLegacyConsentPreferences = ({ region = 'eu' } = {}) => {
  const existing = getConsentPreferences();
  if (existing) return existing;

  const legacyAnalytics = readLegacyBoolean('analytics-consent');
  const legacyMarketing = readLegacyBoolean('email-contact-consent');
  if (legacyAnalytics === null && legacyMarketing === null) {
    return null;
  }

  return setConsentPreferences({
    region,
    categories: {
      necessary: true,
      functional: false,
      analytics: legacyAnalytics === true,
      marketing: legacyMarketing === true,
    },
    decidedAt: new Date().toISOString(),
    source: 'migration',
  });
};

export const ensureConsentPreferences = ({ region = 'eu' } = {}) => {
  const existing = getConsentPreferences();
  if (existing) return existing;

  const migrated = migrateLegacyConsentPreferences({ region });
  if (migrated) return migrated;

  return setConsentPreferences({
    region,
    categories: DEFAULT_CATEGORIES,
    decidedAt: null,
    source: 'migration',
  });
};

export const hasDecidedConsent = () => {
  const preferences = getConsentPreferences();
  return Boolean(preferences?.decided_at);
};

export const isConsentModalSnoozed = () => {
  try {
    const raw = localStorage.getItem(CONSENT_MODAL_SNOOZE_KEY);
    if (!raw) return false;
    const until = Number(raw);
    if (!Number.isFinite(until)) return false;
    return until > Date.now();
  } catch (_) {
    return false;
  }
};

export const snoozeConsentModal = (durationMs = CONSENT_MODAL_SNOOZE_MS) => {
  try {
    localStorage.setItem(CONSENT_MODAL_SNOOZE_KEY, String(Date.now() + durationMs));
  } catch (_) {
    // Best effort only.
  }
};

export const clearConsentModalSnooze = () => {
  try {
    localStorage.removeItem(CONSENT_MODAL_SNOOZE_KEY);
  } catch (_) {
    // Best effort only.
  }
};

export const shouldShowConsentModalForEuUser = () => {
  const preferences = getConsentPreferences();
  if (preferences?.decided_at) return false;
  return !isConsentModalSnoozed();
};

export const getConsentCategories = () => {
  const preferences = getConsentPreferences();
  return preferences?.categories || { ...DEFAULT_CATEGORIES };
};

export const isAnalyticsConsentGranted = () => {
  return getConsentCategories().analytics === true;
};

export const openConsentModal = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(CONSENT_MODAL_OPEN_EVENT));
};

export const setCategoryConsent = ({
  functional,
  analytics,
  marketing,
  source = 'settings',
  decidedAt = new Date().toISOString(),
} = {}) => {
  const current = getConsentPreferences();
  const nextCategories = {
    necessary: true,
    functional: typeof functional === 'boolean' ? functional : current?.categories?.functional === true,
    analytics: typeof analytics === 'boolean' ? analytics : current?.categories?.analytics === true,
    marketing: typeof marketing === 'boolean' ? marketing : current?.categories?.marketing === true,
  };

  return setConsentPreferences({
    region: current?.region || 'eu',
    categories: nextCategories,
    decidedAt,
    source,
  });
};

export const buildConsentPayloadFromPreferences = (preferences = getConsentPreferences()) => {
  if (!preferences) {
    return {
      functional_consent: null,
      analytics_consent: readLegacyBoolean('analytics-consent'),
      marketing_consent: null,
      consent_version: null,
      consent_updated_at: null,
      consent_source: null,
    };
  }

  if (!preferences.decided_at) {
    return {
      functional_consent: null,
      analytics_consent: null,
      marketing_consent: null,
      consent_version: CONSENT_VERSION,
      consent_updated_at: preferences.updated_at,
      consent_source: preferences.source,
    };
  }

  return {
    functional_consent: preferences.categories.functional,
    analytics_consent: preferences.categories.analytics,
    marketing_consent: preferences.categories.marketing,
    consent_version: CONSENT_VERSION,
    consent_updated_at: preferences.updated_at,
    consent_source: preferences.source,
  };
};

const toDateMs = (value) => {
  if (typeof value !== 'string') return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
};

export const mergeServerConsentIfNewer = ({
  profile,
  region = 'eu',
} = {}) => {
  if (!profile) return { applied: false, preferences: getConsentPreferences() };

  const serverUpdatedAt = parseIsoTimestamp(profile.consent_updated_at);
  if (!serverUpdatedAt) {
    return { applied: false, preferences: getConsentPreferences() };
  }

  const localPreferences = getConsentPreferences();
  const localUpdatedMs = toDateMs(localPreferences?.updated_at);
  const serverUpdatedMs = toDateMs(serverUpdatedAt);

  if (localUpdatedMs && serverUpdatedMs && localUpdatedMs >= serverUpdatedMs) {
    return { applied: false, preferences: localPreferences };
  }

  const hasAnyServerDecision =
    profile.functional_consent !== null ||
    profile.analytics_consent !== null ||
    profile.marketing_consent !== null;

  const mergedPreferences = setConsentPreferences({
    region,
    categories: {
      necessary: true,
      functional: profile.functional_consent === true,
      analytics: profile.analytics_consent === true,
      marketing: profile.marketing_consent === true,
    },
    decidedAt: hasAnyServerDecision ? serverUpdatedAt : null,
    source: profile.consent_source || 'server_sync',
    updatedAt: serverUpdatedAt,
  });

  return { applied: true, preferences: mergedPreferences };
};
